/**
 * Ad Naming Convention Utilities
 * 
 * Shared utility for generating ad names from the naming convention
 * configured in ad_account_settings.
 */

// ===== extractBaseName =====

/**
 * Extract the "base name" from a creative filename, stripping dimensions,
 * ratios, and placement suffixes used in asset grouping conventions.
 * 
 * For paired/triple assets: compares filenames to find common base.
 * For single assets: strips known dimension/ratio/placement patterns.
 */
export function extractBaseName(
  filename?: string | null,
  groupType?: string,
  partnerFilename?: string | null
): string {
  if (!filename) return "";

  // Step 1: Strip file extension
  let base = filename.replace(/\.[^/.]+$/, "");

  // Step 2: For paired/triple assets with a partner, use common-prefix approach
  // This is the most robust method — handles ANY naming convention
  if (
    (groupType === "pair" || groupType === "triple") &&
    partnerFilename
  ) {
    let partnerBase = partnerFilename.replace(/\.[^/.]+$/, "");
    
    // Find longest common prefix character by character
    let commonLen = 0;
    const minLen = Math.min(base.length, partnerBase.length);
    for (let i = 0; i < minLen; i++) {
      if (base[i] === partnerBase[i]) {
        commonLen = i + 1;
      } else {
        break;
      }
    }
    
    if (commonLen > 0) {
      let common = base.substring(0, commonLen);
      // Trim trailing separators and spaces
      common = common.replace(/[_\-\s.]+$/, "");
      // Also strip any partial dimension/ratio patterns left over from the prefix cut
      // This handles cases like "Banana_1080x1" where the prefix cuts mid-dimension
      common = common.replace(/[_ -]?\d{1,4}[x:\-]\d{0,4}$/i, "");
      common = common.replace(/[_ -]?\d{1,4}[x:\-]?$/i, "");
      // Trim trailing separators again after stripping
      common = common.replace(/[_\-\s.]+$/, "");
      if (common.length > 0) {
        return common;
      }
    }
  }

  // Step 3: For single assets (or if common-prefix didn't work),
  // strip all known dimension/ratio/placement patterns.
  const DIMENSION_PATTERNS = [
    // Full pixel dimensions: 1080x1080, 1080-1080, 1080:1080, etc.
    /[_ -]?\d{3,4}[x:\-]\d{3,4}/gi,
    // Ratios with separators: 1-1, 9-16, 16-9, 4-5, 1:1, 9:16, 16:9, 4:5
    /[_ -](?:1[:-]1|9[:-]16|16[:-]9|4[:-]5|1x1|9x16|16x9|4x5)(?=[_ .\-]|$)/gi,
    // Named placement suffixes (must be preceded by a separator)
    /[_ -](?:feed|story|infeed|post|landscape|square|vertical|horizontal)(?=[_ .\-]|$)/gi,
    // Short letter codes (must be preceded by a separator, case-insensitive)
    /[_ -](?:G|S|V|H|L)(?=[_ .\-]|$)/g,
  ];
  
  for (const pattern of DIMENSION_PATTERNS) {
    base = base.replace(pattern, "");
  }
  
  // Step 4: Trim trailing/leading separators and spaces
  base = base.replace(/[_\-\s.]+$/, "");
  base = base.replace(/^[_\-\s.]+/, "");

  // Step 5: Fallback — if stripping removed everything, use original without extension
  if (!base || base.length === 0) {
    return filename.replace(/\.[^/.]+$/, "");
  }

  return base;
}

// ===== generateAdName =====

export interface GenerateAdNameParams {
  namingConvention: {
    tags: string[];
    separator: string;
    custom_separator?: string;
    custom_tags?: string[];
    utm_safe?: boolean;
  } | null;
  adFormat?: string;
  creativeFeedFilename?: string | null;
  creativeVerticalFilename?: string | null;
  creativeHorizontalFilename?: string | null;
  groupType?: string;
  creativeType?: string; // 'image' or 'video'
  campaignName?: string;
  adsetName?: string;
  creativeTags?: Record<string, string>;
}

const FORMAT_SHORT: Record<string, string> = {
  "Single Image": "img",
  "Single Video": "vid",
  "Single Image or Video": "img",
  "Carousel": "car",
  "Collection": "col",
  "Catalog": "dpa",
  "Flexible": "flex",
};

const ASSET_TYPE_MAP: Record<string, string> = {
  image: "image",
  video: "video",
};

function resolveFilenameTag(params: GenerateAdNameParams): string {
  const { adFormat, creativeFeedFilename, creativeVerticalFilename, creativeHorizontalFilename, groupType } = params;

  if (adFormat === "Carousel") return "carousel";
  if (adFormat === "Catalog") return "catalog";
  if (adFormat === "Flexible") return "flex";

  // Single or Collection
  if (creativeFeedFilename) {
    return extractBaseName(creativeFeedFilename, groupType, creativeVerticalFilename);
  }
  if (creativeVerticalFilename) {
    return extractBaseName(creativeVerticalFilename);
  }
  if (creativeHorizontalFilename) {
    return extractBaseName(creativeHorizontalFilename);
  }

  return "filename";
}

function resolveAssetType(params: GenerateAdNameParams): string {
  const { creativeType, adFormat } = params;
  if (adFormat === "Catalog") return "catalog";
  if (adFormat === "Carousel" || adFormat === "Collection" || adFormat === "Flexible") return "mix";
  if (creativeType) return ASSET_TYPE_MAP[creativeType] || "asset";
  return "asset";
}

function extractNamePart(displayName: string): string {
  // Strip trailing " (12345)" style suffixes
  const match = displayName.match(/^(.+?)\s*\(\d+\)$/);
  return match ? match[1].trim() : displayName.trim();
}

/**
 * Generate an ad name from the naming convention and row data.
 */
export function generateAdName(params: GenerateAdNameParams): string {
  const { namingConvention, creativeFeedFilename, creativeVerticalFilename, groupType } = params;

  // Fallback: no convention or empty tags → use base filename
  if (!namingConvention || !namingConvention.tags || namingConvention.tags.length === 0) {
    const base = extractBaseName(
      creativeFeedFilename || creativeVerticalFilename,
      groupType,
      creativeVerticalFilename
    );
    return base || "";
  }

  // Determine separator
  const separator = namingConvention.separator === "custom"
    ? (namingConvention.custom_separator || "")
    : namingConvention.separator;

  const customTags = namingConvention.custom_tags || [];

  // Resolve each tag
  const resolvedParts: string[] = [];

  for (const tag of namingConvention.tags) {
    let value: string | null = null;

    // Custom tags (prefixed with "custom:")
    if (tag.startsWith("custom:")) {
      const customText = tag.replace(/^custom:/, "");
      // Only include if it's in the custom_tags list
      if (customTags.includes(customText)) {
        value = customText.replace(/\s+/g, separator);
      }
    }
    // Dynamic tags
    else if (tag === "filename") {
      value = resolveFilenameTag(params);
    } else if (tag === "date") {
      // YYYYMMDD — e.g. 20260307
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      value = `${y}${m}${d}`;
    } else if (tag === "date_us") {
      // MMDDYYYY — e.g. 03072026 (US format: month first)
      const now = new Date();
      value = String(now.getMonth() + 1).padStart(2, "0") + String(now.getDate()).padStart(2, "0") + now.getFullYear();
    } else if (tag === "date_int") {
      // DDMMYYYY — e.g. 07032026 (International format: day first)
      const now = new Date();
      value = String(now.getDate()).padStart(2, "0") + String(now.getMonth() + 1).padStart(2, "0") + now.getFullYear();
    } else if (tag === "ad_format") {
      value = FORMAT_SHORT[params.adFormat || ""] || "ad";
    } else if (tag === "asset_type") {
      value = resolveAssetType(params);
    } else if (tag === "campaign_name") {
      if (params.campaignName) {
        const name = extractNamePart(params.campaignName);
        value = name.replace(/\s+/g, separator);
      }
    } else if (tag === "adset_name") {
      if (params.adsetName) {
        const name = extractNamePart(params.adsetName);
        value = name.replace(/\s+/g, separator);
      }
    }
    // Placeholder tags (from creativeTags)
    else if (["influencer", "product", "offer", "concept"].includes(tag)) {
      if (params.creativeTags?.[tag]) {
        value = params.creativeTags[tag];
      }
      // Skip if not in creativeTags (omit from final name)
    }

    if (value) {
      resolvedParts.push(value);
    }
  }

  if (resolvedParts.length === 0) {
    // Fallback to base filename
    const base = extractBaseName(
      creativeFeedFilename || creativeVerticalFilename,
      groupType,
      creativeVerticalFilename
    );
    return base || "";
  }

  let name = resolvedParts.join(separator);

  // Clean up double separators
  if (separator) {
    const escapedSep = separator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    name = name.replace(new RegExp(`(${escapedSep}){2,}`, "g"), separator);
    // Trim leading/trailing separators
    name = name.replace(new RegExp(`^(${escapedSep})+|(${escapedSep})+$`, "g"), "");
  }

  // UTM-safe sanitization
  const utmSafe = params.namingConvention?.utm_safe === true;
  if (utmSafe && name) {
    name = name.toLowerCase();
    const sep = separator || "_";
    name = name.replace(/\s+/g, sep);
    name = name.replace(/[^a-z0-9\-_|]/g, "");
    if (separator) {
      const escapedSep2 = separator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      name = name.replace(new RegExp(`(${escapedSep2}){2,}`, "g"), separator);
      name = name.replace(new RegExp(`^(${escapedSep2})+|(${escapedSep2})+$`, "g"), "");
    }
  }

  return name;
}
